import { NutritionClient } from 'fitbeat.api-clients';
import { ShoppingListExclusion } from 'fitbeat.models';

import { getApiConfig } from '../config';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { GoogleAnalytics } from './google-analytics';

type UnknownWhenConvertedToTs = any;

export class NutritionService {
  private nutritionClient = new NutritionClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder(),
    new GoogleAnalytics(),
  );

  private isUpdating: boolean;

  private onProfileChanged: (UnknownWhenConvertedToTs) => void;

  private updateQueue: UnknownWhenConvertedToTs[];

  constructor(onProfileChanged) {
    this.onProfileChanged = onProfileChanged;
    this.isUpdating = false;
    this.updateQueue = [];
  }

  public saveFoodPreferences = async (foodPreferences, useImperialUnit, dietGoal, gender) => {
    const responseJson = await this.nutritionClient.saveFoodPreferences(foodPreferences, useImperialUnit,
      dietGoal, gender);
    this.onProfileChanged(responseJson);
    return responseJson;
  }

  public saveUnitPreference = async (useImperialUnit) => {
    const responseJson = await this.nutritionClient.saveUnitPreference(useImperialUnit);
    this.onProfileChanged(responseJson);
    return responseJson;
  }

  public saveDietGoal = async (dietGoal, gender) => {
    const responseJson = await this.nutritionClient.saveDietGoal(dietGoal, gender);
    this.onProfileChanged(responseJson);
    return responseJson;
  }

  public getMealPlan = async () => {
    const responseJson = await this.nutritionClient.getMealPlan();
    return responseJson;
  }

  public getShoppingList = async () => {
    const responseJson = await this.nutritionClient.getShoppingList();
    return responseJson;
  }

  public getMealImage = async (mealId) => {
    const responseJson = await this.nutritionClient.getMealImage(mealId);
    return responseJson;
  }

  public getThumbnail = async (mealId) => {
    const responseJson = await this.nutritionClient.getThumbnailImage(mealId);
    return responseJson;
  }

  public getThumbnails = async (mealPlanId) => {
    const responseJson = await this.nutritionClient.getThumbnails(mealPlanId);
    return responseJson;
  }

  public getMealsForTimeOfDay = async (timeOfDay, mealPlanId) => {
    const responseJson = await this.nutritionClient.getMealsForTimeOfDay(timeOfDay, mealPlanId);
    return responseJson;
  }

  public setUserMealPlan = async (mealId, mealOfDay, weekDay) => {
    const responseJson = await this.nutritionClient.setUserMealPlan(mealId, mealOfDay, weekDay);
    return responseJson;
  }

  public setShoppingListSettings = async (mealPlanWithMeals: ShoppingListExclusion[], servingCount: number) => {
    const responseJson = await this.nutritionClient.setShoppingListSettings(mealPlanWithMeals, servingCount);
    return responseJson;
  }

  public setChecklistExclusions = async (checklistExclusions) => {
    const updateParameter = checklistExclusions;
    const asyncUpdateServiceCall = this.nutritionClient.setChecklistExclusions;
    await this.sendToUpdateQueue(updateParameter, asyncUpdateServiceCall);
  }

  // TODO: move to own class with this.isUpdating and this.updateQueue state so that we can reuse
  public sendToUpdateQueue = async (newUpdateParameter, asyncUpdateServiceCall) => {
    this.updateQueue.unshift(newUpdateParameter);

    if (this.isUpdating) {
      // there is already an update loop in progress, which will process this update
      return;
    }

    try {
      this.isUpdating = true;
      while (this.updateQueue.length > 0) { // keep update loop running till queue empty
        const updateParameter = this.updateQueue.pop();
        await asyncUpdateServiceCall(updateParameter);
      }
    } finally {
      this.isUpdating = false;
    }
  }

  public captureNutritionRating = async (date, rating) => {
    const responseJson = await this.nutritionClient.captureNutritionRating(date, rating);
    return responseJson;
  }

  public getLastNutritionRating = async () => {
    const responseJson = await this.nutritionClient.getLastNutritionRating();
    return responseJson ? responseJson : undefined;
  }
}
